import * as React from "react";
import { useFree30Subjects } from "./use-free30-subjects";
import { useFree30Timeline } from "./use-free30-timeline";
import { classifyFree30Sequence } from "./free30-sequence";
import {
  IFree30Subject,
  IGuestSequence,
  IFree30Message,
  IFree30Specificity
} from "./free30-sequence.types";

function outcomeLabel(s: IFree30Subject): string {
  if (s.converted_at) return "converted";
  if (s.attended_at) return "attended";
  return "no-show";
}

function senderLabel(m: IFree30Message): string {
  if ((m.effectiveDirection || m.direction) === "inbound") return "guest";
  return m.source === "app" ? "coach" : "auto";
}

function specificityLabel(spec: IFree30Specificity): string {
  if (spec.customization_status === "customized") return "specific";
  if (spec.customization_status === "not_customized") return "needs work";
  return "undetermined";
}

function stepText(seq: IGuestSequence): string {
  const part = (label: string, present: boolean, neutral?: boolean) => {
    if (present) return label;
    if (neutral) return "—";
    return `(no ${label})`;
  };
  return [
    "booked",
    part("auto-text", seq.autoText.present),
    part("coach reply", seq.coachReply.present),
    part("guest reply", seq.guestReply.present, !seq.guestReply.present),
    part("call", seq.callBeforeSession.present)
  ].join(" → ");
}

function GuestRow({
  subdomain,
  subject
}: {
  subdomain: string;
  subject: IFree30Subject;
}) {
  const [open, setOpen] = React.useState(false);
  const hasEvidence =
    subject.matched_from_ghl_opportunity || subject.matched_from_pike13_session;
  const {
    messages,
    sessionDate,
    notes,
    compliance,
    specificity,
    loading
  } = useFree30Timeline(
    subdomain,
    subject.pike13_person_id,
    open && hasEvidence
  );

  const seq =
    messages != null
      ? classifyFree30Sequence(messages, sessionDate || subject.session_date)
      : null;

  // Distinct outbound URLs across the conversation (video / page links sent).
  const urls = (messages || [])
    .flatMap(m => m.urls || [])
    .filter((u, i, arr) => arr.findIndex(x => x.url === u.url) === i);

  return (
    <div>
      <button type="button" onClick={() => setOpen(o => !o)}>
        {subject.name || `Person ${subject.pike13_person_id}`} ·{" "}
        {outcomeLabel(subject)}
      </button>
      {!hasEvidence && <div>no contact data captured</div>}
      {open && hasEvidence && (
        <div>
          {loading && <div>loading evidence…</div>}
          {seq && <div>{stepText(seq)}</div>}

          {specificity ? (
            <div>
              Specificity: {specificityLabel(specificity)}
              {specificity.rationale ? ` — ${specificity.rationale}` : ""}
            </div>
          ) : (
            !loading && messages && <div>Specificity: not graded yet</div>
          )}

          {compliance && (
            <div>
              Video / link sent: {compliance.match_video_sent ? "yes" : "no"}
            </div>
          )}
          {urls.length > 0 && (
            <ul>
              {urls.map(u => (
                <li key={u.url}>
                  <a href={u.url} target="_blank" rel="noopener noreferrer">
                    {u.url}
                  </a>
                </li>
              ))}
            </ul>
          )}

          {notes.length > 0 && (
            <div>
              Notes:
              <ul>
                {notes.map(n => (
                  <li key={n.noteId}>
                    {n.sourceDateAdded} · {n.title || "(untitled note)"}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {messages && messages.length > 0 && (
            <div>
              Exchange:
              <ul>
                {messages.map(m => (
                  <li key={m.messageId}>
                    {m.sourceDateAdded} · {senderLabel(m)} · {m.messageType}
                    {m.body ? `: ${m.body}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Free30Drilldown({
  subdomain,
  month,
  coach
}: {
  subdomain: string;
  month: string;
  coach: string | null;
}) {
  const { subjects, loading, error } = useFree30Subjects(
    subdomain,
    month,
    coach,
    true
  );

  if (loading) return <div>loading guests…</div>;
  if (error) return <div>Could not load guests: {error}</div>;
  if (!subjects || subjects.length === 0)
    return <div>No Free 30 guests this month.</div>;

  const unmatched = subjects.filter(
    s => !(s.matched_from_ghl_opportunity || s.matched_from_pike13_session)
  ).length;

  return (
    <div>
      {unmatched > 0 && (
        <div>{unmatched} guest(s) have no matched contact evidence.</div>
      )}
      {subjects.map(s => (
        <GuestRow key={s.subject_id} subdomain={subdomain} subject={s} />
      ))}
    </div>
  );
}
